import { Component, Input, Output, EventEmitter } from '@angular/core';
import { CommonModule } from '@angular/common';
import { LoadingState } from '../../../core/panels/loading-panel.component';
import { CardComponent } from '../../../core/panels/card.component';
import { TableComponent } from '../../../core/table/table.component';
import { ColumnComponent } from '../../../core/table/column.component';
import { DetailPanelComponent } from '../../../core/panels/detail-panel/detail-panel.component';
import { DetailItemComponent } from '../../../core/panels/detail-panel/detail-item.component';
import { ExportFormat } from '../../../core/buttons/export-button.component';
import { BillLinesResponse } from '../../../../models/api/view/claim/financials/bills/bill-lines-response';
import { EmptyStateComponent } from '../../../core/panels/empty-state.component';
import { ProgressbarComponent } from '../../../core/labels/progressbar.component';
import { BillDataTransformer } from '../../../../utilities/bill-data-transformer';

@Component({
  selector: 'app-bill-lines-section',
  standalone: true,
  imports: [
    CommonModule,
    CardComponent,
    TableComponent,
    ColumnComponent,
    DetailPanelComponent,
    DetailItemComponent,
    EmptyStateComponent,
    ProgressbarComponent,
  ],
  template: `
    <app-card label="Bill line items">
      @if (state === 'Loading') {
        <app-empty-state
          height="200px"
          icon="fa-light fa-list"
          label="Loading bill lines"
          message="Retrieving from bill repository"
        >
          <div class="tw-py-4">
            <app-progressbar />
          </div>
        </app-empty-state>
      } @else {
        <app-table
          [dataSource]="billLines"
          [scrollable]="false"
          dataKey="lineNum"
          recordLabel="item"
          [showFilterButton]="false"
          [showFilter]="false"
          [showRecordLabel]="true"
          [showPaginator]="false"
          [showExport]="true"
          (export)="onExport($event)"
          [expandable]="true"
          sortField="lineNum"
          [sortOrder]="1"
        >
          <app-column name="lineNum" label="Line number" [sort]="true" />
          <app-column name="dosBegin" label="Service begin date" format="d" />
          <app-column name="dosEnd" label="Service end date" format="d" />
          <app-column name="billedAmount" label="Billed amount" format="c" />
          <app-column
            name="feeReduction"
            label="Fee schedule reduction"
            format="c"
          />
          <app-column
            name="ppoReduction"
            label="Network reduction"
            format="c"
          />
          <app-column
            name="otherReduction"
            label="Negotiated discount"
            format="c"
          />
          <app-column
            name="recAmount"
            label="Recommended allowance"
            format="c"
          />
          <ng-template #expand let-data>
            <div class="tw-flex">
              <div class="tw-basis-1/3 tw-pr-8">
                <app-detail-panel [dataSource]="data">
                  <app-detail-item label="Pay code" name="payCode" />
                  <app-detail-item label="Service type" name="serviceType" />
                  <app-detail-item
                    label="Procedure description"
                    name="procDescr"
                  />
                </app-detail-panel>
              </div>
              <div class="tw-basis-1/3 tw-pr-8">
                <app-detail-panel [dataSource]="data">
                  <app-detail-item
                    label="Procedure code billed"
                    name="procCodeBilled"
                  />
                  <app-detail-item
                    label="Procedure code paid"
                    name="procCodePaid"
                  />
                  <app-detail-item label="Bill quantity" name="billedQty" />
                </app-detail-panel>
              </div>
              <div class="tw-basis-1/3 tw-pr-8">
                <app-detail-panel [dataSource]="data">
                  <app-detail-item
                    label="Service national provider identifier"
                    name="serviceNPI"
                  />
                  <app-detail-item label="Location" name="serviceLocation" />
                  <app-detail-item label="Line fee" name="lineFee" />
                </app-detail-panel>
              </div>
            </div>
          </ng-template>
        </app-table>
      }
    </app-card>
  `,
})
export class BillLinesSectionComponent {
  @Input() billLinesData: BillLinesResponse | null = null;
  @Input() state!: LoadingState;
  @Output() export = new EventEmitter<ExportFormat>();

  get billLines() {
    return BillDataTransformer.getBillLines(this.billLinesData);
  }

  onExport(format: ExportFormat) {
    this.export.emit(format);
  }
}
